/**
 * resumeHandoff.ts — read the admin plan-link's opaque assessment id (P1E) from
 * the URL, fragment first, and strip the deprecated query form on arrival.
 *
 * WHY fragment, not query: `?resume=<assessmentId>` is a re-entry link straight
 * into CHECKOUT for an already-submitted assessment, emailed to a named patient.
 * A query param is sent to the server on every request for that document, so the
 * id lands in load-balancer + Cloud Armor access logs, in the browser's history,
 * in the `Referer` header on the next hop, and in GTM's `page_location` — where
 * an assessment id for an identifiable person would sit inside a container
 * administered outside this estate. A fragment is never sent to the server at
 * all — the same posture as `#lead=` (leadHandoff.ts) and the pharmacy link
 * token in PharmacyAuthPage.
 *
 * The `?resume=` form is kept as a DEPRECATED read so links already sitting in
 * patients' inboxes still work. Be honest about what the scrub can and cannot
 * do: by the time we run, that first request has already been logged. Scrubbing
 * stops the id being carried onward (history, referrer, page_location, every
 * later in-app navigation), and every link the PORTAL mints already uses the
 * fragment (see `lib/orderResume.ts`).
 *
 * CLOSED AT THE SOURCE ON 2026-08-14 (clinic-service #109): the plan-link
 * handover email now mints `${PORTAL_BASE_URL}/weightloss#resume=${id}`, so no
 * NEW link puts an assessment id into an access log. This comment said the
 * opposite for a month after it stopped being true, which is worse than saying
 * nothing — a reader checking whether the hole was still open would have
 * believed it was.
 *
 * ⚠️ THE FALLBACK STAYS ANYWAY, and the reason is unchanged: every link sent
 * BEFORE 2026-08-14 is sitting in a patient's inbox in the query form, and it
 * still has to work. Deleting the read is what would break them — the mint
 * being fixed does nothing for links already sent. Retire it only once a link
 * from before that date could no longer reach a checkout at all, and never
 * before. Be honest about what the scrub does for those: by the time this code
 * runs, that first request has already been logged; scrubbing only stops the id
 * being carried onward.
 *
 * ALSO STILL OPEN: `?assessment=<assessmentId>` is the SAME identifier and is
 * deliberately put in the query string by the Webflow widget handoff
 * (`embed/main.ts`) and by `stampAssessment` in JourneyPage. Moving `resume`
 * out of the query narrows the exposure; it does not close it.
 */

import type { Location } from 'react-router-dom';
import { stripKeyFromLocation } from './urlKeys';

/** Accept `#resume=<assessmentId>` — the canonical form. */
export function readResumeIdFromHash(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return params.get('resume')?.trim() || null;
}

/** DEPRECATED `?resume=<assessmentId>` — links already in inboxes only. */
export function readLegacyResumeIdFromQuery(search: string): string | null {
  return new URLSearchParams(search).get('resume')?.trim() || null;
}

/** Either form, fragment winning — the one read the journey should use. */
export function readResumeId(
  location: Pick<Location, 'search' | 'hash'>,
): string | null {
  return readResumeIdFromHash(location.hash) ?? readLegacyResumeIdFromQuery(location.search);
}

/**
 * The UUID shape an assessment id always has. This is a SHAPE check, not a
 * validity check — the server still owns whether the id resolves to anything.
 */
const ASSESSMENT_ID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isAssessmentIdShaped(value: string): boolean {
  return ASSESSMENT_ID_SHAPE.test(value);
}

/**
 * A resume entry plus WHAT KIND of value it carries (pinned contract
 * addendum, 2026-09-23): a UUID in the fragment is an assessment id and keeps
 * today's path byte-identical; a NON-UUID fragment value is a short-TTL GRANT
 * the journey must first exchange (`POST …/assessments/resume-exchange`) for
 * the id — the unlock lanes mint `/weightloss#resume=<grant>` so the durable
 * assessment id never rides a WhatsApp thread or a nurture email.
 *
 * ⚠️ THE DEPRECATED `?resume=` QUERY FORM IS NEVER A GRANT. Every query-form
 * link predates grants entirely (the mint moved to the fragment on
 * 2026-08-14, grants arrived 2026-09-23, and nothing has ever minted a grant
 * into a query string) — so a non-UUID query value is a legacy assessment id,
 * and treating it as a grant would 404 every pre-2026-08-14 inbox link this
 * fallback exists to keep alive.
 */
export interface ResumeEntry {
  value: string;
  grant: boolean;
}

export function readResumeEntry(
  location: Pick<Location, 'search' | 'hash'>,
): ResumeEntry | null {
  const fromHash = readResumeIdFromHash(location.hash);
  if (fromHash) return { value: fromHash, grant: !isAssessmentIdShaped(fromHash) };
  const fromQuery = readLegacyResumeIdFromQuery(location.search);
  return fromQuery ? { value: fromQuery, grant: false } : null;
}

/**
 * The search + hash this location would have with every `resume` key removed.
 *
 * PURE, and the only thing this module exposes about removal — the NAVIGATION
 * lives in `handoffScrub.ts`, which owns both hand-off keys and rewrites the
 * address bar exactly once. The address bar can carry TWO hand-off ids and there
 * is only one of it: a `navigate` inside an effect does not refresh
 * `useLocation()` before the next statement runs, so two independent scrubs
 * computed from the same snapshot let the second `replace` write back whatever
 * the first removed — a link carrying `?resume=` AND `?lead=` used to end up
 * with `?resume=` restored. Keeping the navigation out of here is what stops
 * that being rebuildable.
 *
 * Every OTHER key survives byte for byte, `#lead=` (which rides the fragment on
 * widget hand-offs) and a plain `#faq` anchor included — see `urlKeys.ts` for
 * why removal is a segment filter rather than a `URLSearchParams` round-trip.
 */
export function stripResumeKeys(location: Pick<Location, 'search' | 'hash'>): {
  search: string;
  hash: string;
} {
  return stripKeyFromLocation(location, 'resume');
}
